import type { PaymentMethod } from './sales';

/**
 * Supplier payables: what a supplier is owed, and what has been paid against it.
 *
 * Money leaving the business, kept structurally apart from the customer side. A supplier payment is
 * posted or voided and never edited, and what an invoice still owes is derived from the payments
 * allocated to it rather than stored anywhere.
 */

export const SupplierPaymentStatus = {
  POSTED: 'POSTED',
  VOIDED: 'VOIDED',
} as const;
export type SupplierPaymentStatus =
  (typeof SupplierPaymentStatus)[keyof typeof SupplierPaymentStatus];

/**
 * The tender types a supplier payment may use.
 *
 * A subset of `PaymentMethod`, which is a neutral vocabulary shared with the customer side. `WALLET`
 * is a consumer tender with no supplier meaning, and there is deliberately no cheque: a cheque only
 * settles anything once it clears, and that lifecycle does not exist yet.
 */
export const SUPPLIER_PAYMENT_METHODS = ['CASH', 'BANK_TRANSFER', 'CARD', 'OTHER'] as const;
export type SupplierPaymentMethod = (typeof SUPPLIER_PAYMENT_METHODS)[number];

/** Methods that cannot be reconciled later without the provider's own reference. */
export const SUPPLIER_PAYMENT_METHODS_REQUIRING_REFERENCE: readonly SupplierPaymentMethod[] = [
  'BANK_TRANSFER',
  'CARD',
  'OTHER',
];

export function isSupplierPaymentMethod(value: PaymentMethod | string): boolean {
  return (SUPPLIER_PAYMENT_METHODS as readonly string[]).includes(value);
}

export function supplierPaymentNeedsReference(method: SupplierPaymentMethod): boolean {
  return SUPPLIER_PAYMENT_METHODS_REQUIRING_REFERENCE.includes(method);
}

/** The most invoices one payment may settle in a single document. */
export const MAX_SUPPLIER_PAYMENT_ALLOCATIONS = 200;

/**
 * How much of a posted invoice has been paid.
 *
 * Derived on every read from the allocations of posted payments, never stored. A voided invoice has
 * no settlement state at all — its obligation is zero because it was withdrawn, not because anyone
 * paid it.
 */
export const PayableSettlement = {
  UNPAID: 'UNPAID',
  PARTIALLY_PAID: 'PARTIALLY_PAID',
  PAID: 'PAID',
} as const;
export type PayableSettlement = (typeof PayableSettlement)[keyof typeof PayableSettlement];

/** Whether anybody should be chasing this invoice today. Also derived. */
export const PayableDueState = {
  SETTLED: 'SETTLED',
  UPCOMING: 'UPCOMING',
  DUE: 'DUE',
  OVERDUE: 'OVERDUE',
} as const;
export type PayableDueState = (typeof PayableDueState)[keyof typeof PayableDueState];

/** One posted invoice, with everything about its settlement worked out at read time. */
export interface PayableInvoiceSummary {
  purchaseInvoiceId: string;
  reference: string;
  supplierInvoiceNumber: string;
  supplierId: string;
  supplierName: string;
  supplierNameAr: string | null;
  branchId: string;
  branchName: string;
  currencyCode: string;
  invoiceDate: string;
  dueDate: string;
  /** What the document itself says. `POSTED` or `VOIDED`. */
  status: string;
  /** `totalMinor` while posted, zero once voided. */
  obligationMinor: number;
  /** The sum of allocations belonging to posted payments. */
  activeAllocatedMinor: number;
  outstandingMinor: number;
  /** Null for a voided invoice, which has no settlement state. */
  settlement: PayableSettlement | null;
  due: PayableDueState;
  /** Positive while the due date is ahead; null once nothing is outstanding. */
  daysUntilDue: number | null;
  /** Positive once the due date has passed; null while it has not. */
  daysOverdue: number | null;
  /** Only where something is outstanding; null otherwise. */
  ageingBucket: string | null;
}

/** One payment's share of one invoice, as the invoice reads it. */
export interface PayableInvoicePayment {
  supplierPaymentId: string;
  reference: string;
  paymentDate: string;
  method: SupplierPaymentMethod;
  status: SupplierPaymentStatus;
  allocatedMinor: number;
  /** Zero once the payment is voided: the row stays, its effect does not. */
  activeMinor: number;
}

export interface PayableInvoiceDetail extends PayableInvoiceSummary {
  payments: PayableInvoicePayment[];
}

/** One invoice's share of one payment, as the payment reads it. */
export interface SupplierPaymentAllocationSummary {
  id: string;
  purchaseInvoiceId: string;
  reference: string;
  supplierInvoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  invoiceTotalMinor: number;
  allocatedMinor: number;
  /** What the invoice still owes now, after every posted payment. */
  outstandingMinor: number;
}

export interface SupplierPaymentSummary {
  id: string;
  reference: string;
  status: SupplierPaymentStatus;
  supplierId: string;
  supplierName: string;
  supplierNameAr: string | null;
  branchId: string;
  branchName: string;
  currencyCode: string;
  paymentDate: string;
  method: SupplierPaymentMethod;
  /** The sum of its allocations. The server works it out; a client never sends it. */
  amountMinor: number;
  /** What it settles right now: the amount while posted, zero once voided. */
  activeMinor: number;
  allocationCount: number;
  postedByName: string | null;
  postedAt: string;
  voidedAt: string | null;
}

export interface SupplierPaymentDetail extends SupplierPaymentSummary {
  /** The provider's own reference. Present only to a caller allowed to see it. */
  externalReference: string | null;
  voidedReason: string | null;
  voidedByName: string | null;
  allocations: SupplierPaymentAllocationSummary[];
}

/** What a supplier is owed across the branches a caller can reach. */
export interface PayableSupplierPosition {
  supplierId: string;
  supplierName: string;
  supplierNameAr: string | null;
  currencyCode: string;
  outstandingMinor: number;
  overdueMinor: number;
  invoiceCount: number;
  overdueCount: number;
  /** The oldest unpaid due date, which is what someone chases first. */
  oldestDueDate: string | null;
}

export interface PayablesSummary {
  asOf: string;
  currencyCode: string;
  outstandingMinor: number;
  invoiceCount: number;
  dueTodayMinor: number;
  dueTodayCount: number;
  dueSoonMinor: number;
  dueSoonCount: number;
  /** The window `dueSoon` covers, in days after today. */
  dueSoonWithinDays: number;
  upcomingMinor: number;
  upcomingCount: number;
  overdueMinor: number;
  overdueCount: number;
}

export interface PayablesAgeingRow {
  supplierId: string;
  supplierName: string;
  supplierNameAr: string | null;
  buckets: Record<string, number>;
  totalMinor: number;
}

export interface PayablesAgeingReport {
  asOf: string;
  currencyCode: string;
  totals: Record<string, number>;
  totalMinor: number;
  rows: PayablesAgeingRow[];
}
